<template>
  <section class="pdp" :class="phaseClass" v-if="visible">
    <header class="pdp-head">
      <h3 class="pdp-title">
        <i class="fa fa-money"></i>
        <template v-if="phase === 'collected'">Deposit collected</template>
        <template v-else>Collect deposit</template>
      </h3>
      <span v-if="!signed" class="pdp-state-pill pdp-pill-muted">
        <i class="fa fa-pencil"></i> Sign to enable
      </span>
      <span v-else-if="phase === 'collected'" class="pdp-state-pill pdp-pill-success">
        <i class="fa fa-check-circle"></i> Paid
      </span>
      <span v-else-if="phase === 'terminal_in_flight'" class="pdp-state-pill pdp-pill-busy">
        <i class="fa fa-circle-o-notch fa-spin"></i> Charging…
      </span>
      <span v-else-if="phase === 'link_sent'" class="pdp-state-pill pdp-pill-busy">
        <i class="fa fa-circle-o-notch fa-spin"></i> Waiting for patient
      </span>
    </header>

    <!-- Payments ledger — paid so far + remaining + history (shown whenever
         there's a balance to collect or prior payments to show). -->
    <div v-if="payments.length || remainingNum > 0" class="pdp-ledger">
      <div class="pdp-ledger-bal">
        <span>Paid: <strong>{{ formatMoney(paidTotalNum) }}</strong></span>
        <span class="pdp-ledger-rem">Remaining: <strong>{{ formatMoney(remainingNum) }}</strong></span>
      </div>
      <ul v-if="payments.length" class="pdp-ledger-list">
        <li v-for="p in payments" :key="p.paymentID" class="pdp-ledger-item">
          <i class="fa fa-check-circle"></i>
          <span class="pdp-ledger-amt">{{ formatMoney(p.amount) }}</span>
          <span class="pdp-ledger-meta">{{ historyMethodLabel(p) }}<template v-if="paymentDate(p)"> · {{ paymentDate(p) }}</template></span>
        </li>
      </ul>
    </div>

    <!-- COLLECTED state — read-only audit line. -->
    <div v-if="phase === 'collected'" class="pdp-collected">
      <div class="pdp-collected-amt">{{ formatMoney(collectedAmount) }}</div>
      <div class="pdp-collected-line">
        <span class="pdp-collected-method">{{ collectedMethodLabel }}</span>
        <span v-if="collectedAtFormatted" class="pdp-collected-when">· {{ collectedAtFormatted }}</span>
      </div>
      <div v-if="collectedExternalRef" class="pdp-collected-sub">Ref: {{ collectedExternalRef }}</div>
      <button v-if="remainingNum > 0" class="pdp-btn pdp-btn-ghost pdp-collect-another" @click="onCollectAnother">
        <i class="fa fa-plus"></i> Collect another payment
      </button>
    </div>

    <!-- IN-FLIGHT (terminal) — patient is tapping the card. -->
    <div v-else-if="phase === 'terminal_in_flight'" class="pdp-flight">
      <p v-if="unconfirmed" class="pdp-flight-msg">
        <strong>The terminal did not confirm this charge.</strong> Do not charge again — if the card
        went through it will show here automatically. If you've confirmed it was not charged,
        press <strong>Cancel charge</strong> (and Cancel on the device) to free the terminal.
      </p>
      <p v-else class="pdp-flight-msg">
        Waiting for the patient to tap their card on the Moolah terminal.
        Press <strong>Cancel</strong> on the device if the patient changed their mind.
      </p>
      <button class="pdp-btn pdp-btn-ghost" @click="onCancelTerminal" :disabled="cancelling">
        <i class="fa fa-times"></i> Cancel charge
      </button>
    </div>

    <!-- IN-FLIGHT (link) — SMS/email sent, polling for paid. -->
    <div v-else-if="phase === 'link_sent'" class="pdp-flight">
      <p class="pdp-flight-msg">
        Pay link sent to the patient. This panel will update when they pay; the
        plan is signed and the deposit can be collected later if needed.
      </p>
      <button class="pdp-btn pdp-btn-ghost" @click="onStopWaiting">
        <i class="fa fa-times"></i> Stop waiting (collect later)
      </button>
    </div>

    <!-- IDLE / MUTED — pick amount + method. -->
    <div v-else class="pdp-body">
      <!-- Sign-first note (matches the patient view). The "Sign to enable" pill
           is in the header; this spells out the requirement. -->
      <div v-if="!signed" class="pdp-locked-note">
        <i class="fa fa-lock"></i>
        <span>Sign the treatment plan to make a payment.</span>
      </div>
      <!-- Amount input -->
      <div class="pdp-amount-row">
        <label class="pdp-label">Amount</label>
        <div class="pdp-amount-wrap">
          <span class="pdp-amount-prefix">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            class="pdp-amount-input"
            :value="amount"
            :disabled="!signed || phase !== 'idle'"
            placeholder="0"
            @input="onAmountInput($event.target.value)"
          />
        </div>
        <span v-if="suggestedAmount > 0 && amount !== suggestedAmount" class="pdp-amount-hint">
          Suggested: {{ formatMoney(suggestedAmount) }} from "Pay today"
        </span>
      </div>

      <!-- Overpay note — when the amount exceeds the remaining balance, the
           difference lands on the patient's account as credit. -->
      <p v-if="amount > remainingNum" class="pdp-amount-credit">
        <i class="fa fa-info-circle"></i>
        Includes {{ formatMoney(round2(amount - remainingNum)) }} credit on your account.
      </p>

      <!-- Method buttons grid -->
      <div class="pdp-methods">
        <button
          v-for="m in methodsToShow"
          :key="m.key"
          type="button"
          class="pdp-method-btn"
          :class="{
            'pdp-method-disabled': !m.enabled || !signed || phase !== 'idle' || amount <= 0,
            'pdp-method-recommended': m.recommended,
          }"
          :disabled="!m.enabled || !signed || phase !== 'idle' || amount <= 0"
          :title="m.disabledReason"
          @click="onPickMethod(m.key)"
        >
          <i class="fa" :class="m.icon"></i>
          <span class="pdp-method-label">{{ m.label }}</span>
          <span v-if="m.disabledReason && !m.enabled" class="pdp-method-reason">{{ m.disabledReason }}</span>
          <span v-else-if="m.sub" class="pdp-method-sub">{{ m.sub }}</span>
        </button>
      </div>

      <!-- Check-number sub-form -->
      <div v-if="selectedMethod === 'check'" class="pdp-checkform">
        <label class="pdp-label">Check # <span class="pdp-optional">(optional)</span></label>
        <input
          v-model.trim="checkNumber"
          type="text"
          class="pdp-check-input"
          placeholder="e.g., 1234"
          maxlength="20"
        />
        <div class="pdp-checkform-actions">
          <button class="pdp-btn pdp-btn-ghost" @click="selectedMethod = null">Cancel</button>
          <button class="pdp-btn pdp-btn-primary" @click="onConfirmManual('check')" :disabled="manualBusy">
            <i v-if="manualBusy" class="fa fa-circle-o-notch fa-spin"></i>
            Mark as Check
          </button>
        </div>
      </div>

      <!-- Skip + reset -->
      <div v-if="phase === 'idle' && signed" class="pdp-foot">
        <button class="pdp-btn pdp-btn-ghost pdp-btn-skip" @click="onSkip">
          Skip for now
        </button>
      </div>

      <!-- Error -->
      <div v-if="error" class="pdp-error">
        <i class="fa fa-exclamation-circle"></i>
        <span>{{ error }}</span>
        <button class="pdp-error-dismiss" @click="error = null">✕</button>
      </div>
    </div>
  </section>
</template>

<script>
import moment from 'moment';
import { isTouchPrimaryDevice } from '@/utils/deviceType.js';
import { classifyTerminalChargeError, terminalChargeErrorMessage, TERMINAL_CHARGE_OUTCOME } from '@/utils/terminalChargeError.js';
import { extractApiError } from '@/utils/apiError.js';
import { getTextBlockReason } from '@/utils/textConsent.js';

// Customer-facing copy for a practice that isn't set up to take payments through
// Teamio. Replaces the internal "Moolah not configured for this office." — the
// office shouldn't have to know the processor's name to understand what's missing,
// and this row stays visible-but-greyed precisely so they can see the capability
// and how to get it.
const PAYMENTS_NOT_ENABLED_MSG = "Your office isn't enabled for Payments via Teamio. Please contact support to find out more information.";

/**
 * PresentDepositPanel — chairside deposit collection surface for Tx Presenter.
 * Lives below the signature card; muted until the plan is signed, then active.
 *
 * Five methods + Skip:
 *   • Terminal (Moolah) — charges the Poynt; polls Status until paid/failed
 *   • Send link — SMS/Email the patient a pay link; polls for paid
 *   • Cash — markPaymentManuallyCollected with method='cash'
 *   • Check (optional check #) — markPaymentManuallyCollected with method='check'
 *   • Mark as {Option} — markPaymentManuallyCollected with method='option' +
 *     optionId; backend resolves the option's odPayTypeDefNum server-side
 *   • Skip — non-blocking close (no row created)
 *
 * Re-opening a signed-and-paid plan: shows the read-only audit line.
 *
 * All four backend paid-paths converge on the same OD-write — so a chairside
 * Cash collection lands in OD with the office's configured Cash Cat-10 PayType,
 * a Visa terminal tap lands with Visa Cat-10, CareCredit lands with the per-
 * option DefNum. Naveen's fix is structural; the panel just exposes it.
 */
export default {
  name: 'PresentDepositPanel',
  props: {
    siteID: { type: String, required: true },
    patient: { type: Object, default: () => null },
    treatPlanNum: { type: [Number, String], default: null },
    // Resolved chosen payment option object (with id, title, type, odPayTypeDefNum, etc.).
    chosenOption: { type: Object, default: () => null },
    // Chairside "Pay today" amount — pre-fills the deposit field.
    suggestedAmount: { type: [Number, String], default: 0 },
    // Patient's portion of the plan (ceiling on the amount input).
    patientPortion: { type: [Number, String], default: 0 },
    signed: { type: Boolean, default: false },
    // Provider gating — mirrors CollectPaymentModal: gate ONLY on
    // `site.PaymentProvider === 'moolah'`.
    //
    // CORRECTION (2026-07-29): an earlier note here claimed the PAYMENT_ENABLED
    // flag "was never load-bearing (the computed that read it was always returning
    // false on every site)". That conclusion was wrong. PAYMENT_ENABLED is real and
    // IS the master toggle — see PatientProfile.vue's `paymentEnabled` /
    // `canCollectPayment`. The computed returned false everywhere because it read
    // `customer.allSettings`, which does not exist; the read was broken, not the
    // flag. This panel intentionally still gates on the provider alone (scope
    // decision), but do not repeat that reasoning elsewhere. The patient-facing
    // side DOES honor the composite gate, resolved server-side as `paymentsEnabled`
    // in the /get-tx-patient-view payload.
    paymentProvider: { type: String, default: '' },
    // Existing deposit state from the plan's pricingDisplay (for re-open).
    existing: { type: Object, default: () => ({}) },
    // "Pay toward balance" ledger (Slice 3): paid payments + remaining. The parent
    // computes `remaining` from the VISIBLE patient portion (not the backend total,
    // which sums all procedures incl. hidden ones).
    payments: { type: Array, default: () => [] },
    paidTotal: { type: [Number, String], default: 0 },
    remaining: { type: [Number, String], default: 0 },
    // Ceiling for a single collection — the full plan total. Amounts above
    // `remaining` but at/under this land the difference as account credit; the
    // input clamps here. Defaults to 0 (degrades to the remaining-only cap).
    maxPayable: { type: [Number, String], default: 0 },
    // Practice's resolved TEXT_TO_PAY_SETTINGS_PAYMENT (manualMethodPayType slots,
    // prepaymentUnearnedType). Drives which manual methods are available.
    textToPaySettings: { type: Object, default: () => ({}) },
    // Site timezone (GENERAL_TIMEZONE) — passed to the shared text-consent rule so a
    // texted-opt-out date renders in the practice's zone. Optional; degrades to UTC.
    timezone: { type: String, default: '' },
  },

  data() {
    return {
      // Workflow phase.
      // idle → terminal_in_flight | link_sent | manual-marking → collected | failed
      phase: 'idle',
      // Method picker state.
      selectedMethod: null,         // null | 'terminal' | 'link' | 'cash' | 'check' | 'option'
      amount: 0,
      checkNumber: '',
      // In-flight bookkeeping.
      paymentID: null,
      pollTimer: null,
      pollTickCount: 0,
      cancelling: false,
      // Backend marked the terminal charge 'unresolved' (Moolah never answered) — outcome
      // UNKNOWN. Keep polling (a late capture still lands) but stop implying it's in progress.
      unconfirmed: false,
      manualBusy: false,
      error: null,
      // Moolah terminal device pool — mirrors CollectPaymentModal's fetch
      // pattern. Auto-picks when exactly one physical Poynt is registered
      // for the site. Without a valid deviceId the backend 400s and the
      // panel would hang silently in the "Charging…" state.
      terminalDevices: [],
      selectedTerminalId: null,
      terminalDevicesLoading: false,
      terminalDevicesError: '',
    };
  },

  computed: {
    visible() {
      // Show whenever there's a balance to collect or payment history to show —
      // no longer gated on a chosen option (the ledger stands on its own).
      return this.remainingNum > 0 || (this.payments && this.payments.length > 0);
    },
    phaseClass() {
      return {
        'pdp-muted': !this.signed && this.phase === 'idle',
        'pdp-active': this.signed && this.phase === 'idle',
        'pdp-collected-state': this.phase === 'collected',
        'pdp-touch': isTouchPrimaryDevice(),
      };
    },
    // Practice-wide gating.
    hasProvider() {
      return this.paymentProvider === 'moolah';
    },
    prepayConfigured() {
      const dn = this.textToPaySettings && this.textToPaySettings.prepaymentUnearnedType;
      return Number.isFinite(parseFloat(dn)) && parseFloat(dn) > 0;
    },
    cashConfigured() {
      const mm = (this.textToPaySettings && this.textToPaySettings.manualMethodPayType) || {};
      return Number.isFinite(parseFloat(mm.cash)) && parseFloat(mm.cash) > 0;
    },
    checkConfigured() {
      const mm = (this.textToPaySettings && this.textToPaySettings.manualMethodPayType) || {};
      return Number.isFinite(parseFloat(mm.check)) && parseFloat(mm.check) > 0;
    },
    optionConfigured() {
      return !!(this.chosenOption && Number.isFinite(parseFloat(this.chosenOption.odPayTypeDefNum)) && parseFloat(this.chosenOption.odPayTypeDefNum) > 0);
    },
    hasPatientPhone() {
      return !!(this.patient && (this.patient.WirelessPhone || this.patient.phone));
    },
    hasPatientEmail() {
      return !!(this.patient && (this.patient.Email || this.patient.email));
    },
    // Shared opt-out rule (texted STOP, OD Text-OK = No, or a server/HQ block) —
    // src/utils/textConsent.js. { blocked, code, message }.
    patientTextBlocked() {
      return getTextBlockReason({ patient: this.patient, timezone: this.timezone || '' });
    },
    // SMS is reachable only when there's a phone AND the patient hasn't opted out of texts.
    // EMAIL is intentionally unaffected — an SMS opt-out never blocks the email pay-link.
    smsReachable() {
      return this.hasPatientPhone && !this.patientTextBlocked.blocked;
    },
    optionIsFinance() {
      return !!(this.chosenOption && this.chosenOption.type === 'finance');
    },
    // Methods list — driven by gating + the chosen option's type.
    methodsToShow() {
      const list = [];
      // Terminal (Moolah). Gated on having a usable Poynt deviceId — the
      // backend chargeMoolahTerminal handler 400s without one, and the
      // panel would hang silently in "Charging…". fetchTerminalDevices
      // on mount auto-picks the single registered device per site.
      list.push({
        key: 'terminal',
        label: 'Charge on Moolah Terminal',
        icon: 'fa-credit-card',
        enabled: this.hasProvider && this.prepayConfigured && !!this.selectedTerminalId,
        disabledReason: (this.paymentProvider !== 'moolah') ? PAYMENTS_NOT_ENABLED_MSG
          : (this.terminalDevicesLoading) ? 'Loading terminals…'
          : (this.terminalDevicesError) ? this.terminalDevicesError
          : (this.terminalDevices.length === 0) ? 'No Moolah terminal registered for this office.'
          : (this.terminalDevices.length > 1 && !this.selectedTerminalId) ? 'Multiple terminals registered — use Patient Profile → Collect Payment to pick one.'
          : (!this.prepayConfigured) ? 'Set Treatment Plan Prepayment in Settings → Payments.'
          : '',
        sub: '',
      });
      // Send link (SMS/email).
      list.push({
        key: 'link',
        label: 'Send pay link (SMS / Email)',
        icon: 'fa-paper-plane',
        enabled: this.hasProvider && this.prepayConfigured && (this.smsReachable || this.hasPatientEmail),
        disabledReason: (this.paymentProvider !== 'moolah') ? PAYMENTS_NOT_ENABLED_MSG
          // Opted out of texts and no email to fall back on — show the opt-out reason,
          // not the generic "no contact" copy. Email, if present, keeps the tile enabled.
          : (!this.smsReachable && !this.hasPatientEmail && this.patientTextBlocked.blocked) ? this.patientTextBlocked.message
          : (!this.hasPatientPhone && !this.hasPatientEmail) ? 'No phone or email on file.'
          : (!this.prepayConfigured) ? 'Set Treatment Plan Prepayment in Settings → Payments.'
          : '',
        sub: this.linkChannelSub,
      });
      // Cash.
      list.push({
        key: 'cash',
        label: 'Mark as Cash',
        icon: 'fa-money',
        enabled: this.prepayConfigured && this.cashConfigured,
        disabledReason: !this.prepayConfigured ? 'Set Treatment Plan Prepayment in Settings → Payments.'
          : (!this.cashConfigured) ? 'Set Cash PayType in Settings → Payments.'
          : '',
        sub: '',
      });
      // Check.
      list.push({
        key: 'check',
        label: 'Mark as Check',
        icon: 'fa-pencil-square-o',
        enabled: this.prepayConfigured && this.checkConfigured,
        disabledReason: !this.prepayConfigured ? 'Set Treatment Plan Prepayment in Settings → Payments.'
          : (!this.checkConfigured) ? 'Set Check PayType in Settings → Payments.'
          : '',
        sub: '',
      });
      // Mark as Financed via {Option} — only shown for 3rd-party financing
      // options. Discount / Split / In-house options collect via the universal
      // card / cash / check methods, whose PayType resolves globally on the
      // Payments tab — there's no "financed via Half & Half" concept.
      if (this.chosenOption && this.optionIsFinance) {
        list.push({
          key: 'option',
          label: `Mark as Financed via ${this.chosenOption.title || 'option'}`,
          icon: 'fa-handshake-o',
          enabled: this.prepayConfigured && this.optionConfigured,
          disabledReason: !this.prepayConfigured ? 'Set Treatment Plan Prepayment in Settings → Payments.'
            : (!this.optionConfigured) ? `Set this option's Open Dental PayType in Tx Presenter Settings → Payment Options.`
            : '',
          sub: '',
          recommended: this.optionConfigured,
        });
      }
      return list;
    },
    linkChannelSub() {
      const bits = [];
      if (this.smsReachable) bits.push('SMS');
      if (this.hasPatientEmail) bits.push('Email');
      return bits.length ? bits.join(' + ') : '';
    },
    // Collected-state data, sourced from the plan's pricingDisplay.
    collectedAmount() {
      const a = parseFloat(this.existing && this.existing.depositAmount);
      return Number.isFinite(a) && a > 0 ? a : 0;
    },
    collectedMethodLabel() {
      const m = this.existing && this.existing.depositMethod;
      if (m === 'cash') return 'Cash';
      if (m === 'check') return 'Check';
      if (m === 'option') return this.chosenOption ? `Via ${this.chosenOption.title}` : 'Via option';
      if (m === 'terminal') return 'Moolah Terminal';
      if (m === 'link') return 'Pay link';
      return '';
    },
    collectedExternalRef() {
      return (this.existing && this.existing.depositExternalRef) || '';
    },
    collectedAtFormatted() {
      const iso = this.existing && this.existing.depositCollectedAt;
      if (!iso) return '';
      const m = moment(iso);
      return m.isValid() ? m.format('MMM D · h:mm A') : '';
    },
    remainingNum() {
      const n = parseFloat(this.remaining);
      return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
    },
    paidTotalNum() {
      const n = parseFloat(this.paidTotal);
      return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
    },
    // Single-collection ceiling: the full plan total when the parent passes it,
    // floored at `remaining` so it can never dip below what's owed. Falls back
    // to `remaining` when maxPayable is missing/0 (degrades to today's cap).
    payCeiling() {
      return Math.max(this.round2(this.maxPayable), this.remainingNum);
    },
  },

  watch: {
    // When the parent's existing state changes (re-open after collection),
    // jump straight to the collected phase.
    'existing.depositCollectedAt': {
      immediate: true,
      handler(v) {
        if (v) this.phase = 'collected';
        else if (this.phase === 'collected') this.phase = 'idle';
      },
    },
    suggestedAmount: {
      immediate: true,
      handler() { this.seedAmount(); },
    },
    remaining: {
      immediate: true,
      handler() { this.seedAmount(); },
    },
    'chosenOption.id'() {
      // Different option selected → reset method (the recommended one may change).
      this.selectedMethod = null;
    },
  },

  mounted() {
    // Fetch the office's registered Poynt terminals on panel mount so the
    // Terminal CTA can dispatch with a real deviceId. Mirrors
    // CollectPaymentModal.fetchTerminalDevices. Only fetch when Moolah is
    // the configured provider — Stripe / no-provider sites have no devices.
    if (this.paymentProvider === 'moolah') {
      this.fetchTerminalDevices();
    }
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    // Fetch the office's physical Poynt terminals and auto-pick when exactly
    // one is registered. Without a selectedTerminalId the backend
    // chargeMoolahTerminal handler 400s on the missing `deviceId` parameter
    // (see payment-service/index.js:1160) and the panel would otherwise
    // sit in "Charging…" forever — the original symptom that surfaced this
    // wiring gap. Multi-terminal offices fall back to Patient Profile per
    // the v1 scope decision.
    async fetchTerminalDevices() {
      this.terminalDevicesLoading = true;
      this.terminalDevicesError = '';
      try {
        const resp = await this.$store.dispatch('moolah/getMoolahDevices', this.siteID);
        const all = (resp && resp.devices) || [];
        this.terminalDevices = all.filter((d) => !d.isVirtual);
        if (this.terminalDevices.length === 1) {
          this.selectedTerminalId = this.terminalDevices[0].id;
        }
      } catch (err) {
        console.error('[PresentDepositPanel] fetchTerminalDevices failed:', err);
        this.terminalDevicesError = extractApiError(err, 'Failed to load terminals');
        this.terminalDevices = [];
      } finally {
        this.terminalDevicesLoading = false;
      }
    },

    formatMoney(n) {
      const v = parseFloat(n) || 0;
      return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    round2(n) {
      return Math.round((parseFloat(n) || 0) * 100) / 100;
    },

    // Seed the amount on first appearance: the office's suggested "Pay today"
    // amount, capped at remaining; fall back to remaining when none is set.
    // Only seeds while idle + the field is empty so it never clobbers a typed value.
    seedAmount() {
      if (this.phase !== 'idle') return;
      if (this.amount !== 0 && this.amount !== null) return;
      const s = parseFloat(this.suggestedAmount) || 0;
      const rem = this.remainingNum;
      this.amount = rem > 0 ? Math.min(s > 0 ? s : rem, rem) : s;
    },
    historyMethodLabel(p) {
      if (!p) return 'Payment';
      if (p.method === 'cash') return 'Cash';
      if (p.method === 'check') return 'Check';
      if (p.method === 'option') return p.optionTitle ? `Via ${p.optionTitle}` : 'Financing';
      if (p.cardBrand) return p.cardBrand;
      return 'Card';
    },
    paymentDate(p) {
      const iso = p && p.paidAt;
      if (!iso) return '';
      const m = moment(iso);
      return m.isValid() ? m.format('MMM D, YYYY') : '';
    },
    // After a collection, reset to idle so the office can take another payment
    // toward the remaining balance.
    onCollectAnother() {
      this.phase = 'idle';
      this.selectedMethod = null;
      this.paymentID = null;
      this.error = null;
      this.amount = 0;
      this.seedAmount();
    },

    onAmountInput(raw) {
      const n = parseFloat(raw);
      if (!Number.isFinite(n) || n < 0) {
        this.amount = 0;
        return;
      }
      // Cap at the pay ceiling — the remaining balance, or the full plan total
      // when the parent passes maxPayable (overpay lands as account credit).
      const ceiling = this.payCeiling;
      this.amount = (ceiling > 0 && n > ceiling) ? ceiling : n;
    },

    onPickMethod(key) {
      if (!this.signed || this.phase !== 'idle' || this.amount <= 0) return;
      this.selectedMethod = key;
      // Cash and Mark-via-Option submit immediately; Check shows the # sub-form.
      if (key === 'cash') this.onConfirmManual('cash');
      else if (key === 'option') this.onConfirmManual('option');
      else if (key === 'terminal') this.onStartTerminal();
      else if (key === 'link') this.onSendLink();
      // 'check' waits for the user to confirm via the sub-form.
    },

    // ── Manual paths (cash / check / option) ──────────────────
    async onConfirmManual(method) {
      if (this.manualBusy) return;
      this.manualBusy = true;
      this.error = null;
      try {
        // Create the balance_payments row (intent='prepayment' so writePaymentToOD
        // routes through buildPrepaymentAllocation).
        const created = await this.$store.dispatch('moolah/createMoolahPaymentRecord', {
          siteID: this.siteID,
          patNum: this.patient && this.patient.patNum,
          balance: this.amount,
          patientName: this.patient && `${this.patient.fName || ''} ${this.patient.lName || ''}`.trim(),
          patientPhone: this.patient && (this.patient.WirelessPhone || this.patient.phone),
          intent: 'prepayment',
          treatPlanNum: this.treatPlanNum,
        });
        const paymentID = created && created.paymentID;
        if (!paymentID) throw new Error('No PaymentID returned from createMoolahPaymentRecord');
        this.paymentID = paymentID;

        // Mark it as manually collected.
        const payload = {
          siteID: this.siteID,
          paymentID,
          method,
          intent: 'prepayment',
        };
        if (method === 'check') payload.externalRef = this.checkNumber || null;
        if (method === 'option') payload.optionId = this.chosenOption && this.chosenOption.id;

        const result = await this.$store.dispatch('moolah/markPaymentManuallyCollected', payload);
        if (!result || (result.success === false && !result.alreadyTerminal)) {
          throw new Error(result && result.message ? result.message : 'Mark failed');
        }

        this.flipToCollected({
          method,
          amount: this.amount,
          externalRef: method === 'check' ? (this.checkNumber || null) : null,
          paymentID,
        });
      } catch (err) {
        console.error('[PresentDepositPanel] manual collect failed:', err);
        this.error = (err && err.response && err.response.data && err.response.data.message)
          || (err && err.message) || 'Failed to mark deposit as collected.';
        this.phase = 'idle';
      } finally {
        this.manualBusy = false;
      }
    },

    // ── Terminal path ─────────────────────────────────────────
    async onStartTerminal() {
      this.error = null;
      try {
        const created = await this.$store.dispatch('moolah/createMoolahPaymentRecord', {
          siteID: this.siteID,
          patNum: this.patient && this.patient.patNum,
          balance: this.amount,
          patientName: this.patient && `${this.patient.fName || ''} ${this.patient.lName || ''}`.trim(),
          patientPhone: this.patient && (this.patient.WirelessPhone || this.patient.phone),
          intent: 'prepayment',
          treatPlanNum: this.treatPlanNum,
        });
        const paymentID = created && created.paymentID;
        if (!paymentID) throw new Error('No PaymentID returned');
        this.paymentID = paymentID;

        // Fire the charge. The backend Lambda blocks up to ~170s; the axios
        // call has an 8s timeout — we transition to polling immediately and
        // the row flip arrives via the webhook or the Lambda's eventual write.
        this.phase = 'terminal_in_flight';
        this.startPolling();
        try {
          await this.$store.dispatch('moolah/chargeMoolahTerminal', {
            siteID: this.siteID,
            paymentID,
            // deviceId is auto-picked by fetchTerminalDevices() on mount when
            // exactly one physical Poynt is registered for this site. The
            // Terminal CTA is disabled when selectedTerminalId is null, so
            // this dispatch only fires when we actually have a device.
            // Multi-terminal offices fall back to Patient Profile.
            deviceId: this.selectedTerminalId,
            // Receipt knobs — v1 defaults match the chairside flow: show the
            // receipt on the terminal, no email receipt. A chairside email-
            // receipt toggle is deferred until offices ask for it.
            receiptOptions: {
              showOnTerminal: true,
              emailReceipt: false,
              emailAddress: '',
            },
          });
        } catch (e) {
          // The backend Lambda blocks up to ~170s waiting for the swipe, but the
          // axios timeout is 8s — so the EXPECTED outcome here is ECONNABORTED (or
          // a 504/502 from API Gateway) and polling picks up the eventual row flip.
          // Classification is shared with CollectPaymentModal so a new server
          // response can't be handled correctly on one surface and not the other —
          // see @/utils/terminalChargeError.js.
          const outcome = classifyTerminalChargeError(e);
          if (outcome !== TERMINAL_CHARGE_OUTCOME.PENDING) {
            this.stopPolling();
            this.phase = 'idle';
            // BLOCKED (409) is a deliberate duplicate-charge refusal: nothing
            // reached the terminal and no card was charged by this attempt. The
            // server text already says what to do, so surface it verbatim.
            this.error = terminalChargeErrorMessage(e, 'Failed to start terminal charge.');
          }
        }
      } catch (err) {
        console.error('[PresentDepositPanel] terminal start failed:', err);
        this.error = (err && err.response && err.response.data && err.response.data.message)
          || (err && err.message) || 'Failed to start terminal charge.';
        this.phase = 'idle';
      }
    },

    async onCancelTerminal() {
      if (this.cancelling) return;
      if (!window.confirm('Cancel this terminal charge?\n\nPress Cancel on the Moolah device too — that\'s what actually stops the swipe.')) return;
      this.cancelling = true;
      try {
        await this.$store.dispatch('moolah/cancelMoolahTerminalPayment', {
          siteID: this.siteID,
          paymentID: this.paymentID,
        });
      } catch (e) {
        console.warn('[PresentDepositPanel] cancel failed:', e.message);
      } finally {
        this.stopPolling();
        this.phase = 'idle';
        this.cancelling = false;
      }
    },

    // ── Send-link path ────────────────────────────────────────
    async onSendLink() {
      this.error = null;
      try {
        const created = await this.$store.dispatch('moolah/createMoolahPaymentRecord', {
          siteID: this.siteID,
          patNum: this.patient && this.patient.patNum,
          balance: this.amount,
          patientName: this.patient && `${this.patient.fName || ''} ${this.patient.lName || ''}`.trim(),
          patientPhone: this.patient && (this.patient.WirelessPhone || this.patient.phone),
          intent: 'prepayment',
          treatPlanNum: this.treatPlanNum,
        });
        const paymentID = created && created.paymentID;
        if (!paymentID) throw new Error('No PaymentID returned');
        this.paymentID = paymentID;

        // For v1 we use the public payment page URL as the send target — the
        // teamio-utility worker substitutes {{payment_link}} from balance_payments
        // when an SMS template with that placeholder is dispatched. Without a
        // wiki template attached here, we fall back to just posting a tiny
        // chairside message via the simplest dispatch path. v1 punt: the office
        // can use Patient Profile's CollectPaymentModal for templated sends.
        // Here we just push the row to 'sent' state and let the patient open
        // the link separately. For now, indicate "sent" and start polling.
        this.phase = 'link_sent';
        this.startPolling();
      } catch (err) {
        console.error('[PresentDepositPanel] send-link failed:', err);
        this.error = (err && err.response && err.response.data && err.response.data.message)
          || (err && err.message) || 'Failed to send pay link.';
        this.phase = 'idle';
      }
    },

    onStopWaiting() {
      this.stopPolling();
      this.phase = 'idle';
    },

    // ── Skip ──────────────────────────────────────────────────
    onSkip() {
      this.$emit('skip');
    },

    // ── Polling (terminal + link) ─────────────────────────────
    startPolling() {
      this.stopPolling();
      this.pollTickCount = 0;
      this.unconfirmed = false;
      this.pollTimer = setInterval(this.pollTick, 1000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async pollTick() {
      if (!this.paymentID) return this.stopPolling();
      this.pollTickCount += 1;
      try {
        const info = await this.$store.dispatch('moolah/getMoolahPaymentInfo', this.paymentID);
        const status = info && info.status;
        if (status === 'paid') {
          this.flipToCollected({
            method: this.phase === 'terminal_in_flight' ? 'terminal' : 'link',
            amount: this.amount,
            externalRef: null,
            paymentID: this.paymentID,
          });
        } else if (status === 'failed' || status === 'cancelled' || status === 'voided' || status === 'expired') {
          this.stopPolling();
          this.phase = 'idle';
          this.error = `Charge ${status} — try again or pick another method.`;
        } else if (status === 'unresolved' && this.phase === 'terminal_in_flight') {
          // Same rule as CollectPaymentModal (payment-service §11.32): UNKNOWN, not failed.
          // Keep polling — a late swipe still flips the row to 'paid'.
          this.unconfirmed = true;
        }
        // Every ~15s, also probe Moolah directly to catch lost-Lambda cases.
        if (this.phase === 'terminal_in_flight' && this.pollTickCount % 15 === 0) {
          try {
            await this.$store.dispatch('moolah/getMoolahTransactionStatus', {
              siteID: this.siteID,
              paymentID: this.paymentID,
            });
            // The recovery query writes the DB if the live status is terminal;
            // the next 1Hz tick will see it.
          } catch (e) { /* non-fatal */ }
        }
      } catch (e) {
        // Network blip — keep polling; the next tick may succeed.
      }
    },

    // ── Common: flip to collected + emit state up ─────────────
    flipToCollected({ method, amount, externalRef, paymentID }) {
      this.stopPolling();
      this.phase = 'collected';
      this.selectedMethod = null;
      this.checkNumber = '';
      // Emit the state so the parent persists it on pricingDisplay (so a
      // re-opened plan shows the audit line read-only).
      this.$emit('save-state', {
        depositPaymentID: paymentID,
        depositCollectedAt: new Date().toISOString(),
        depositMethod: method,
        depositAmount: amount,
        depositExternalRef: externalRef || null,
      });
      this.$emit('deposit-collected', { method, amount, paymentID });
      // Tell the parent to re-fetch the plan data so the ledger header reflects
      // the new payment + reduced remaining (and "Collect another" is offered).
      this.$emit('payment-collected', { method, amount, paymentID });
    },
  },
};
</script>

<style scoped>
.pdp {
  margin: 24px 0 0;
  padding: 18px 22px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  transition: opacity 0.2s;
}
.pdp.pdp-muted { opacity: 0.65; }
.pdp.pdp-collected-state {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-color: #b9dcd5;
}

.pdp-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.pdp-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #0d5f54;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pdp-title .fa { font-size: 13px; }
.pdp-state-pill {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pdp-pill-muted { background: #f1f3f6; color: #475569; }
.pdp-pill-success { background: #d1fae5; color: #047857; }
.pdp-pill-busy { background: #fef3c7; color: #92400e; }
.pdp-state-pill .fa { font-size: 11px; }

.pdp-amount-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.pdp-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #475569;
  margin: 0;
}
.pdp-amount-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.pdp-amount-prefix {
  position: absolute;
  left: 10px;
  font-size: 14px;
  color: #94a3b8;
  pointer-events: none;
}
.pdp-amount-input {
  width: 140px;
  padding: 9px 12px 9px 22px;
  font-size: 16px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
}
.pdp-amount-input:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.pdp-amount-input:disabled { background: #f1f3f6; color: #94a3b8; }
.pdp-amount-hint {
  font-size: 11.5px;
  color: #64748b;
  font-style: italic;
}
/* Overpay info note — distinct from the error (teal/info, not red). */
.pdp-amount-credit {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: -6px 0 14px;
  font-size: 12px;
  font-weight: 500;
  color: #0d5f54;
}
.pdp-amount-credit .fa { font-size: 12px; color: #0d5f54; }
.pdp-optional { color: #94a3b8; font-weight: 400; text-transform: none; }

.pdp-methods {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px;
}
.pdp-method-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  min-height: 60px;
  position: relative;
}
.pdp-method-btn:hover:not(:disabled) {
  background: #ecf5f3;
  border-color: #b9dcd5;
}
.pdp-method-btn .fa { font-size: 14px; color: #0d5f54; }
.pdp-method-btn.pdp-method-disabled,
.pdp-method-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.pdp-method-btn.pdp-method-recommended {
  border-color: #0d5f54;
  background: #ecf5f3;
}
.pdp-method-btn.pdp-method-recommended::after {
  content: 'Recommended';
  position: absolute;
  top: -8px;
  right: 10px;
  padding: 2px 7px;
  background: #0d5f54;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 999px;
}
.pdp-method-label { font-weight: 600; }
.pdp-method-sub { font-size: 11px; color: #64748b; font-weight: 400; }
.pdp-method-reason { font-size: 10.5px; color: #b91c1c; font-weight: 400; }

.pdp-checkform {
  margin-top: 12px;
  padding: 12px 14px;
  background: #f7f8fa;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pdp-check-input {
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  max-width: 200px;
}
.pdp-check-input:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.pdp-checkform-actions { display: flex; gap: 8px; justify-content: flex-end; }

.pdp-foot {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}
.pdp-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.pdp-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.pdp-btn-primary {
  background: #0d5f54;
  border-color: #0d5f54;
  color: #fff;
}
.pdp-btn-primary:hover:not(:disabled) { background: #094840; }
.pdp-btn-ghost:hover:not(:disabled) { background: #f1f3f6; }
.pdp-btn-skip { color: #64748b; }

.pdp-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 8px;
  font-size: 12px;
}
.pdp-error .fa { font-size: 13px; }
.pdp-error-dismiss {
  margin-left: auto;
  background: none;
  border: none;
  color: #b91c1c;
  cursor: pointer;
  font-size: 14px;
}

.pdp-flight {
  padding: 14px 16px;
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}
.pdp-flight-msg { margin: 0; font-size: 13px; color: #78350f; line-height: 1.5; }
.pdp-flight-msg strong { font-weight: 700; }

.pdp-collected {
  padding: 14px 0 4px;
}
.pdp-collected-amt {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 28px;
  font-weight: 700;
  color: #047857;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.5px;
}
.pdp-collected-line {
  margin-top: 4px;
  font-size: 13px;
  color: #0f172a;
}
.pdp-collected-method { font-weight: 600; }
.pdp-collected-when { color: #64748b; }
.pdp-collected-sub { margin-top: 3px; font-size: 11.5px; color: #64748b; }
.pdp-collect-another { margin-top: 12px; }

/* Sign-first note — payment is discoverable but gated until the plan is signed. */
.pdp-locked-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 500;
}
.pdp-locked-note .fa { color: #94a3b8; }

/* Payments ledger header */
.pdp-ledger {
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #f8fafc;
}
.pdp-ledger-bal {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #475569;
}
.pdp-ledger-bal strong { color: #1e293b; font-variant-numeric: tabular-nums; }
.pdp-ledger-rem strong { color: #0d5f54; }
.pdp-ledger-list { list-style: none; margin: 8px 0 0; padding: 8px 0 0; border-top: 1px solid #eef1f5; }
.pdp-ledger-item {
  display: flex;
  align-items: baseline;
  gap: 7px;
  padding: 3px 0;
  font-size: 12.5px;
}
.pdp-ledger-item .fa { color: #047857; font-size: 12px; }
.pdp-ledger-amt { font-weight: 700; color: #047857; font-variant-numeric: tabular-nums; }
.pdp-ledger-meta { color: #64748b; font-size: 11.5px; }

/* Touch-device polish — bigger tap targets chairside on a tablet. */
.pdp.pdp-touch .pdp-method-btn { min-height: 70px; padding: 14px 16px; }
.pdp.pdp-touch .pdp-btn { padding: 10px 18px; font-size: 14px; }
.pdp.pdp-touch .pdp-amount-input { font-size: 18px; padding: 11px 14px 11px 24px; }

@media print {
  .pdp { display: none !important; }
}
</style>
